import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useSignNdaForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import {
  getGetDealTrackingForGpQueryKey,
  useUpdateDealOpportunityTracking,
} from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { NdaSignatureAction } from "@vega-api/generated/types/ndaSignatureAction";
import { NdaTrackingStatus } from "@vega-api/generated/types/ndaTrackingStatus";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type ConfirmNdaSignedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  trackingItem: DealTrackingLimitedPartnerBreakdown | null;
  dealId: string;
  /**
   * How "Signed" is persisted. The legacy tables call the dedicated sign
   * endpoint (`signEndpoint`, default). The new AG-Grid Process table treats
   * NDA as a tracking field, so it writes via `updateDealOpportunityTracking`
   * (`trackingStatus`).
   */
  confirmVia?: "signEndpoint" | "trackingStatus";
};

export const ConfirmNdaSignedModal = ({
  isOpen,
  onClose,
  trackingItem,
  dealId,
  confirmVia = "signEndpoint",
}: ConfirmNdaSignedModalProps) => {
  const { trackEvent } = useAnalytics();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const { mutate: signNda, isPending: isSigning } =
    useSignNdaForDealOpportunityGp();
  const { mutate: updateTracking, isPending: isUpdatingTracking } =
    useUpdateDealOpportunityTracking();

  const isLoading = isSigning || isUpdatingTracking;

  const handleConfirm = useCallback(() => {
    if (!trackingItem) return;

    const onSuccess = () => {
      // Track GP signed NDA on behalf of LP
      trackEvent(AnalyticsEvent.COINVEST_NDA_SIGNED_BY_GP, undefined, {
        dealId,
        dealOpportunityId: trackingItem.dealOpportunityId,
        limitedPartnerId: trackingItem.limitedPartnerId,
      });

      onClose();
      showToast({
        variant: "success",
        title: m.coInvestments_nda_signSuccess(),
      });
      queryClient.invalidateQueries({
        queryKey: getGetDealTrackingForGpQueryKey(dealId),
      });
    };

    const onError = (error: ErrorType<unknown>) => {
      showErrorToast(error, {
        defaultMessage: m.coInvestments_error_signNda(),
      });
    };

    if (confirmVia === "trackingStatus") {
      updateTracking(
        {
          dealOpportunityId: trackingItem.dealOpportunityId,
          data: { ndaTrackingStatus: NdaTrackingStatus.SIGNED },
        },
        { onSuccess, onError },
      );

      return;
    }

    signNda(
      {
        dealOpportunityId: trackingItem.dealOpportunityId,
        data: {
          action: NdaSignatureAction.SIGN,
        },
      },
      { onSuccess, onError },
    );
  }, [
    trackingItem,
    confirmVia,
    signNda,
    updateTracking,
    onClose,
    showToast,
    showErrorToast,
    trackEvent,
    dealId,
    queryClient,
  ]);

  if (!trackingItem) return null;

  return (
    <ActionaryModal
      dataTestId="confirm-nda-signed-modal"
      description={
        <span className="body-base-regular text-text/secondary">
          {m.coInvestments_tracking_confirmNdaSignedModalDescription()}
        </span>
      }
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_tracking_confirmNdaSigned()}
    >
      <div className="flex justify-end gap-3 pt-4">
        <ThemedButton
          dataTestId="cancel-nda-confirmation-button"
          disabled={isLoading}
          onClick={onClose}
          size="default"
          type="button"
          variant="secondary"
        >
          {m.btn_cancel()}
        </ThemedButton>
        <ThemedButton
          dataTestId="confirm-nda-signed-button"
          isLoading={isLoading}
          onClick={handleConfirm}
          size="default"
          type="button"
          variant="primary"
        >
          {m.btn_confirm()}
        </ThemedButton>
      </div>
    </ActionaryModal>
  );
};
